// Cliente de la API de pre-radicación (Solicitudes de radicación / "Expedientes no
// iniciados"). Ver backend src/modules/pre-radicacion.
import { api, subir } from './api';

// «OTRO» se eliminó (ago-06, pedido del cliente): ya no se usa ese canal.
export type Canal = 'CORREO' | 'FISICO' | 'SAIA';
// Decisiones del comité de queja (ago-06, dictadas por el cliente). Los tres
// valores legados (PROCEDE/NO_PROCEDE/SE_OMITE) siguen en el enum de BD por los
// datos existentes, pero ya NO se ofrecen en la UI.
export type DecisionComite =
  | 'APERTURA' | 'INDAGACION' | 'INHIBITORIO' | 'REMISION_COMPETENCIA' | 'OTRO'
  | 'PROCEDE' | 'NO_PROCEDE' | 'SE_OMITE';
export type EstadoSolicitud = 'EN_PROCESO' | 'RADICADA' | 'NO_RADICADA';
export type RutaSolicitud = 'REPARTO' | 'COMITE';
export type ModalidadFisico = 'VERBAL' | 'FISICA';
export type MotivoNoProcede = 'COMPETENCIA' | 'TEMERARIA' | 'OTRO';
// Quién interpone la notificación disciplinaria (mismo enum de la Etapa 2).
export type TipoIngresoQueja = 'DE_OFICIO' | 'QUEJOSO' | 'INFORME' | 'ANONIMO';
export type EstadoActa =
  | 'BORRADOR'
  | 'PENDIENTE_SUSTANCIADOR'
  | 'PENDIENTE_DIRECTOR'
  | 'FIRMADA'
  | 'DEVUELTA_TECNICO';
export type CategoriaAdjunto =
  | 'SAIA'
  | 'CORREO'
  | 'PRINCIPAL'
  | 'DOCUMENTOS'
  | 'ANEXOS'
  | 'MEDIO_MAGNETICO'
  | 'TRANSCRIPCION'
  | 'VU_CORREO'
  | 'VU_DOCUMENTOS'
  | 'VU_RESPUESTA'
  | 'DECISION_COMITE'
  | 'COMITE_ACTA'
  | 'COMITE_CONSTANCIA';

// Orden pedido por el cliente: SAIA · Correo · Físico.
export const CANALES: Array<{ v: Canal; label: string }> = [
  { v: 'SAIA', label: 'SAIA' },
  { v: 'CORREO', label: 'Correo' },
  { v: 'FISICO', label: 'Físico' },
];

// Opciones que ofrece la UI (en este orden). Las dos primeras CONTINÚAN el flujo.
export const DECISIONES: Array<{ v: DecisionComite; label: string }> = [
  { v: 'APERTURA', label: 'Apertura' },
  { v: 'INDAGACION', label: 'Indagación' },
  { v: 'INHIBITORIO', label: 'Inhibitorio' },
  { v: 'REMISION_COMPETENCIA', label: 'Remisión por competencia' },
  { v: 'OTRO', label: 'Otro' },
];

// Etiquetas de TODAS las decisiones (incluye las legadas, para poder mostrarlas).
export const DECISION_COMITE_LABEL: Record<DecisionComite, string> = {
  APERTURA: 'Apertura',
  INDAGACION: 'Indagación',
  INHIBITORIO: 'Inhibitorio',
  REMISION_COMPETENCIA: 'Remisión por competencia',
  OTRO: 'Otro',
  PROCEDE: 'Procede',
  NO_PROCEDE: 'No procede',
  SE_OMITE: 'Se omite',
};

// Decisiones que TERMINAN el proceso (espejo de `decision-comite.ts` del backend).
const DECISIONES_CIERRAN: DecisionComite[] = [
  'INHIBITORIO', 'REMISION_COMPETENCIA', 'OTRO', 'NO_PROCEDE',
];
/** ¿La decisión termina el proceso (no pasa a Etapa 2)? */
export function comiteCierra(d?: DecisionComite | '' | null): boolean {
  return !!d && DECISIONES_CIERRAN.includes(d);
}
/** ¿La decisión permite continuar (acta de reparto → Etapa 2)? */
export function comiteContinua(d?: DecisionComite | '' | null): boolean {
  return !!d && !comiteCierra(d);
}

export const MOTIVOS_NO_PROCEDE: Array<{ v: MotivoNoProcede; label: string }> = [
  { v: 'COMPETENCIA', label: 'Por competencia' },
  { v: 'TEMERARIA', label: 'Temeraria' },
  { v: 'OTRO', label: 'Otro' },
];

export const ESTADO_ACTA_LABEL: Record<EstadoActa, string> = {
  BORRADOR: 'Borrador',
  PENDIENTE_SUSTANCIADOR: 'Pendiente del abogado sustanciador',
  PENDIENTE_DIRECTOR: 'Pendiente de firma del director',
  FIRMADA: 'Firmada por el director',
  DEVUELTA_TECNICO: 'Devuelta para corregir',
};

export function labelCanal(v?: string | null): string {
  return CANALES.find((c) => c.v === v)?.label ?? (v ?? '—');
}
export function labelDecision(v?: string | null): string {
  return v ? (DECISION_COMITE_LABEL[v as DecisionComite] ?? v) : '—';
}

// Adjunto de la solicitud (N por categoría).
export interface AdjuntoSolicitud {
  id: string;
  categoria: CategoriaAdjunto;
  storage_key: string;
  nombre: string;
  mime?: string | null;
  tamano?: number | null;
  orden: number;
}

// Paso de la trazabilidad (ruta ordenada de intervinientes).
export interface PasoTrazabilidad {
  id?: string;
  orden: number;
  usuario_id: string;
  etiqueta?: string | null;
}

// Solicitud tal como la devuelve el backend (snake_case + completa/faltantes).
export interface Solicitud {
  id: string;
  fecha_ingreso: string;
  canal: Canal;
  por_ventanilla_unica: boolean;
  fecha_respuesta_ventanilla?: string | null;
  radicado_origen?: string | null;
  consecutivo_adicional?: number | null;
  radicado_completo?: string | null;
  documento_key?: string | null;
  anexo_key?: string | null;
  // Canal físico + aportes + ventanilla única
  modalidad_fisico?: ModalidadFisico | null;
  transcripcion_texto?: string | null;
  observaciones?: string | null; // en FÍSICO reemplazan al radicado
  aporto_documentos?: boolean;
  aporto_medio_magnetico?: boolean;
  vu_radicado?: string | null;
  // ¿Quién interpone la notificación disciplinaria? Se captura en la Etapa 1 (sea
  // cual sea el canal) y precarga el Quejoso al radicar la Etapa 2, donde todavía
  // se puede completar/corregir.
  tipo_ingreso_queja?: TipoIngresoQueja | null;
  quejoso_nombre?: string | null;
  quejoso_tipo_documento?: string | null;
  quejoso_numero_documento?: string | null;
  quejoso_direccion?: string | null;
  quejoso_municipio?: string | null;
  quejoso_departamento?: string | null;
  quejoso_telefono?: string | null;
  quejoso_correo?: string | null;
  // Ruta y comité
  ruta?: RutaSolicitud | null;
  justificacion_reparto?: string | null;
  fecha_comite?: string | null;
  comite_reprogramado?: boolean;
  fecha_comite_nueva?: string | null;
  decision_comite?: DecisionComite | null;
  no_procede_motivo?: MotivoNoProcede | null;
  no_procede_motivo_otro?: string | null;
  justificacion_comite?: string | null;
  comite_remision_destino?: string | null;
  comite_otro_detalle?: string | null;
  comite_key?: string | null;
  fecha_decision_comite?: string | null;
  // Asignación / acta
  abogado_id?: string | null;
  tecnologo_apoyo_id?: string | null;
  tecnico_id?: string | null;
  director_id?: string | null;
  fecha_asignacion?: string | null;
  plazo_abogado_fecha?: string | null;
  acta_key?: string | null;
  // Cadena de aprobación del acta
  acta_estado?: EstadoActa | null;
  sustanciador_id?: string | null;
  acta_observacion?: string | null;
  acta_visto_bueno_en?: string | null;
  acta_firmada_en?: string | null;
  // Asignación final («Casos aprobados»)
  fecha_reparto?: string | null;
  asignacion_enviada?: boolean;
  estado_solicitud: EstadoSolicitud;
  expediente_id?: string | null;
  fecha_creacion: string;
  completa: boolean;
  faltantes: string[];
  // Solo en el DETALLE (GET :id): adjuntos por categoría y trazabilidad ordenada.
  adjuntos?: AdjuntoSolicitud[];
  trazabilidad?: PasoTrazabilidad[];
  // Nombre de quien asignó (técnico administrativo); solo en la lista de casos asignados.
  asignado_por_nombre?: string | null;
  // Nombres resueltos (solo en bandeja de actas / casos aprobados).
  tecnico_nombre?: string | null;
  abogado_nombre?: string | null;
  sustanciador_nombre?: string | null;
  director_nombre?: string | null;
}

export interface Firmante {
  id: string;
  nombre: string;
  rol: string;
  tieneFirma: boolean;
  firmaKey?: string | null; // storage_key de la imagen de firma (para el acta)
}

export interface CrearSolicitudInput {
  fechaIngreso: string;
  canal: Canal;
  radicadoOrigen?: string;
  porVentanillaUnica?: boolean;
  documentoKey?: string;
  anexoKey?: string;
}

export interface ActualizarSolicitudInput {
  fechaIngreso?: string;
  canal?: Canal;
  radicadoOrigen?: string;
  porVentanillaUnica?: boolean;
  fechaRespuestaVentanilla?: string;
  documentoKey?: string;
  anexoKey?: string;
  modalidadFisico?: ModalidadFisico;
  transcripcionTexto?: string;
  observaciones?: string;
  aportoDocumentos?: boolean;
  aportoMedioMagnetico?: boolean;
  vuRadicado?: string;
  ruta?: RutaSolicitud;
  justificacionReparto?: string;
  fechaComite?: string;
  comiteReprogramado?: boolean;
  fechaComiteNueva?: string;
  noProcedeMotivo?: MotivoNoProcede;
  noProcedeMotivoOtro?: string;
  decisionComite?: DecisionComite;
  justificacionComite?: string;
  comiteRemisionDestino?: string;
  comiteOtroDetalle?: string;
  comiteKey?: string;
  fechaDecisionComite?: string;
  abogadoId?: string;
  tecnologoApoyoId?: string;
  tecnicoId?: string;
  directorId?: string;
  sustanciadorId?: string;
  fechaAsignacion?: string;
  plazoAbogadoFecha?: string;
}

export function listarSolicitudes(estado?: EstadoSolicitud): Promise<Solicitud[]> {
  return api<Solicitud[]>(`/solicitudes${estado ? `?estado=${estado}` : ''}`);
}
/** Casos asignados al abogado/tecnólogo de apoyo, pendientes de llenar la Etapa 2. */
export function listarAsignadas(): Promise<Solicitud[]> {
  return api<Solicitud[]>('/solicitudes/asignadas');
}
export function obtenerSolicitud(id: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}`);
}
export function crearSolicitud(body: CrearSolicitudInput): Promise<Solicitud> {
  return api<Solicitud>('/solicitudes', { method: 'POST', body });
}
export function actualizarSolicitud(id: string, body: ActualizarSolicitudInput): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}`, { method: 'PATCH', body });
}
export function generarActa(id: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/acta`, { method: 'POST' });
}
export function radicarSolicitud(id: string, expedienteId: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/radicar`, { method: 'POST', body: { expedienteId } });
}
export function listarFirmantes(): Promise<Firmante[]> {
  return api<Firmante[]>('/solicitudes/firmantes');
}

// ── Adjuntos (N archivos por categoría; cada subida es un POST) ──
export function subirAdjunto(
  solicitudId: string,
  file: File,
  categoria: CategoriaAdjunto,
): Promise<AdjuntoSolicitud> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('categoria', categoria);
  return subir<AdjuntoSolicitud>(`/solicitudes/${solicitudId}/adjuntos`, fd);
}
export function eliminarAdjunto(solicitudId: string, adjuntoId: string): Promise<unknown> {
  return api(`/solicitudes/${solicitudId}/adjuntos/${adjuntoId}`, { method: 'DELETE' });
}

// ── Cadena de aprobación del acta de reparto ──
/** Envía el acta a revisión. Sin `sustanciadorId` queda para CUALQUIER abogado sustanciador. */
export function enviarActa(id: string, sustanciadorId?: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/acta/enviar`, {
    method: 'POST',
    body: sustanciadorId ? { sustanciadorId } : {},
  });
}
export function aprobarActa(id: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/acta/aprobar`, { method: 'POST' });
}
/** Historial completo de actas de reparto (todas; los filtros van en el front). */
export function listarActasTodas(): Promise<Solicitud[]> {
  return api<Solicitud[]>('/solicitudes/actas/todas');
}
export function devolverActa(id: string, motivo: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/acta/devolver`, { method: 'POST', body: { motivo } });
}
export function firmarActa(id: string): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/acta/firmar`, { method: 'POST' });
}
/** Bandeja del sustanciador (revisar) y del director (firmar). */
export function bandejaActas(): Promise<Solicitud[]> {
  return api<Solicitud[]>('/solicitudes/actas/bandeja');
}
/** «Casos aprobados» del técnico: actas firmadas, a asignar el proceso. */
export function listarAprobadas(): Promise<Solicitud[]> {
  return api<Solicitud[]>('/solicitudes/aprobadas');
}
export interface AsignarProcesoInput {
  fechaReparto: string;
  fechaAsignacion: string;
  plazoAbogadoFecha: string;
  abogadoId?: string;
  tecnologoApoyoId?: string;
  trazabilidad: Array<{ usuarioId: string; etiqueta?: string }>;
}
export function asignarProceso(id: string, body: AsignarProcesoInput): Promise<Solicitud> {
  return api<Solicitud>(`/solicitudes/${id}/asignar`, { method: 'POST', body });
}

/**
 * CALENDARIO de comités de queja: una entrada por solicitud enviada a comité,
 * ubicada en la fecha en que quedó programado (la REPROGRAMADA si la hubo).
 */
export interface EventoComite {
  solicitud_id: string;
  fecha: string;
  reprogramado: boolean;
  radicado: string | null;
  canal: Canal;
  decision?: DecisionComite | null;
  estado_solicitud: EstadoSolicitud;
}

export function calendarioComites(): Promise<EventoComite[]> {
  return api<EventoComite[]>('/solicitudes/calendario');
}

/**
 * Qué se muestra en el día: SOLO el radicado. Cuando la solicitud no tiene
 * (hoy el canal FÍSICO no maneja radicado) se indica por dónde ingresó.
 */
export function etiquetaEvento(e: EventoComite): string {
  return e.radicado ?? `Ingresó por ${labelCanal(e.canal).toLowerCase()}`;
}
